import { useRef } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';

import { AdsPlacement } from 'types/enums/AdsPlacement';
import { AdsPlatform } from 'types/enums/AdsPlatform';
import { ChatAds } from 'types/interfaces/ads/ChatAds';
import { FinderDoubleVerticalAds } from 'types/interfaces/ads/FinderDoubleVerticalAds';
import { FinderHorizontalAds } from 'types/interfaces/ads/FinderHorizontalAds';
import { FinderVerticalAds } from 'types/interfaces/ads/FinderVerticalAds';
import { FloatingBubbleAds } from 'types/interfaces/ads/FloatingBubbleAds';
import { InterstitialAds } from 'types/interfaces/ads/InterstitialAds';
import { NotificationsHorizontalAds } from 'types/interfaces/ads/NotificationsHorizontalAds';
import { PageVerticalAds } from 'types/interfaces/ads/PageVerticalAds';
import { PopupAds } from 'types/interfaces/ads/PopupAds';
import { ThreadsAds } from 'types/interfaces/ads/ThreadsAds';

import { UserTransferApi } from 'api/UserTransferApi';

type Placement =
  | AdsPlacement.FinderHorizontal
  | AdsPlacement.FinderVertical
  | AdsPlacement.Threads
  | AdsPlacement.Chat
  | AdsPlacement.Interstitial
  | AdsPlacement.AdVideoPopup
  | AdsPlacement.FloatingBubble
  | AdsPlacement.NotificationsHorizontal
  | AdsPlacement.FinderDoubleVertical
  | AdsPlacement.MyProfileHorizontal;

interface Response {
  [AdsPlacement.FinderHorizontal]: FinderHorizontalAds;
  [AdsPlacement.FinderVertical]: FinderVerticalAds;
  [AdsPlacement.Threads]: ThreadsAds;
  [AdsPlacement.Chat]: ChatAds;
  [AdsPlacement.Interstitial]: InterstitialAds;
  [AdsPlacement.AdVideoPopup]: PopupAds;
  [AdsPlacement.FloatingBubble]: FloatingBubbleAds;
  [AdsPlacement.NotificationsHorizontal]: NotificationsHorizontalAds;
  [AdsPlacement.FinderDoubleVertical]: FinderDoubleVerticalAds;
  [AdsPlacement.MyProfileHorizontal]: PageVerticalAds;
}

const QUERY_NAMES = {
  [AdsPlacement.FinderHorizontal]: 'finder-horizontal-ads',
  [AdsPlacement.FinderVertical]: 'finder-vertical-ads',
  [AdsPlacement.Threads]: 'threads-ads',
  [AdsPlacement.Chat]: 'chat-ads',
  [AdsPlacement.Interstitial]: 'interstitial-ads',
  [AdsPlacement.AdVideoPopup]: 'popup-ads',
  [AdsPlacement.FloatingBubble]: 'floating-bubble-ads',
  [AdsPlacement.NotificationsHorizontal]: 'notifications-horizontal-ads',
  [AdsPlacement.FinderDoubleVertical]: 'desktop_finder_doubleprofile',
  [AdsPlacement.MyProfileHorizontal]: 'desktop_user_profile_vertical',
};

const QUERY_FUNCTIONS = {
  [AdsPlacement.FinderHorizontal]: UserTransferApi.fetchFinderHorizontalBanner,
  [AdsPlacement.FinderVertical]: UserTransferApi.fetchFinderVerticalBanner,
  [AdsPlacement.Threads]: UserTransferApi.fetchDialogsListBanner,
  [AdsPlacement.Chat]: UserTransferApi.fetchDialogBanner,
  [AdsPlacement.Interstitial]: UserTransferApi.fetchInterstitialBanner,
  [AdsPlacement.AdVideoPopup]: UserTransferApi.fetchVideoPopupBanner,
  [AdsPlacement.FloatingBubble]: UserTransferApi.fetchFloatingBubbleBanner,
  [AdsPlacement.NotificationsHorizontal]:
    UserTransferApi.fetchNotificationsHorizontalBanner,
  [AdsPlacement.FinderDoubleVertical]:
    UserTransferApi.fetchFinderDoubleVerticalBanner,
  [AdsPlacement.MyProfileHorizontal]: UserTransferApi.fetchPageVerticalBanner,
};

interface Props<P> {
  placement: P;
  platform: AdsPlatform;
  index?: number;
}

export const useAdsQuery = <P extends Placement>(
  props: Props<P>,
  queryOptions?: UseQueryOptions<Response[P]>
) => {
  const startRequestTS = useRef<number>(Date.now());

  const queryName = props.index
    ? `${QUERY_NAMES[props.placement]}/${props.index}`
    : QUERY_NAMES[props.placement];
  const queryFn = QUERY_FUNCTIONS[props.placement] as ({
    platform,
  }: {
    platform: AdsPlatform;
  }) => Promise<Response[P]>;

  const adsQuery = useQuery<Response[P]>(
    queryName,
    () => {
      startRequestTS.current = Date.now();

      return queryFn({ platform: props.platform });
    },
    {
      retry: false,
      cacheTime: 0,
      ...queryOptions,
    }
  );

  return {
    adsQuery,
    startRequestTS: startRequestTS.current,
  };
};
